import classnames from 'classnames'
import { useField } from 'formik'
import React, { Suspense, useCallback } from 'react'

import Field from '../Field/Field'
import FieldError from '../FieldError/FieldError'

import styles from './MarkdownEditor.module.scss'

const Editor = React.lazy(() => import('./Editor'))

/**********************************************************************
 * MarkdownEditor
 **********************************************************************/
type MarkdownEditorProps = {
  name?: string
  label?: string
  hideLabel?: boolean
  omitErrorDisplay?: boolean
  className?: string
  touched?: boolean
  error?: string

  disabled?: boolean
  required?: boolean
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
}

const MarkdownEditor = React.memo(function MarkdownEditor({
  label,
  hideLabel,
  omitErrorDisplay,
  className,
  touched,
  error,
  disabled,
  value,
  onChange,
  onBlur,
}: MarkdownEditorProps) {
  return (
    <div className={classnames(styles.MarkdownEditor, className)} onBlur={onBlur}>
      <Field label={hideLabel ? undefined : label} hasError={Boolean(touched && error)} disabled={disabled}>
        <div className={styles.container}>
          <Suspense>
            <Editor value={value} onChange={onChange} />
          </Suspense>
        </div>
      </Field>
      <FieldError touched={touched} error={error} omitErrorDisplay={omitErrorDisplay} />
    </div>
  )
})

export default MarkdownEditor

/**********************************************************************
 * MarkdownEditorFormik
 **********************************************************************/
type MarkdownEditorFormikProps = { name: string } & Omit<
  MarkdownEditorProps,
  'name' | 'touched' | 'error' | 'value' | 'onChange'
>
export const MarkdownEditorFormik = React.memo(function MarkdownEditorFormik({
  name,
  ...componentProps
}: MarkdownEditorFormikProps) {
  const validate = useCallback(
    (value: any) =>
      !componentProps.required || (value && String(value).trim()) ? undefined : 'Please fill out this field',
    [componentProps.required]
  )

  const [field, meta, _helpers] = useField({ name, validate })

  const handleChange = useCallback(
    (value: string) => {
      field.onChange(name)(value)
    },
    [field.onChange]
  )

  const handleBlur = useCallback(() => field.onBlur(name)({ target: {} }), [field.onBlur])

  return (
    <MarkdownEditor
      {...componentProps}
      {...field}
      touched={meta.touched}
      error={meta.error}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
})
